import { Action } from "re-reduced";
import { SagaIterator } from "redux-saga";
import { call, put, select } from "redux-saga/effects";

import { getErrorMessage } from "@lib/graphql-error";
import actions from "@state/actions";
import {
  getEventId, getGateId, getOrgId, getOnline, getScanDismissed,
  getScanDirection, getSelectedEvent, getUser
} from "@state/selectors";
import { DeviceTicket, ScanPayload, Event } from "@state/types";
import { User, FeatureFlagKeyEnum, ScanTicketMutation, BuyerInformation, Ticket, ProblemCode, ScanStatus } from "@graphql/sdk";
import * as api from "@util/api";
import { determineTicketStatus } from "@util/helper";
import { ScanTicketParams } from "@util/interfaces";
import scanDeviceTicket from "./scanDeviceTicket";

export default function* scanTicketOnline(action: Action<ScanPayload>): SagaIterator {
  yield put(actions.scanTicketOnline.request());
  const online: boolean = yield select(getOnline);
  if (!online) {
    yield call(scanDeviceTicket, actions.scanDeviceTicket(action.payload));
    return;
  }
  
  const ticketNumber = action.payload.ticketNumber;
  const scanType = action.payload.scanType;
  const bypassValidations = action.payload.bypassValidations;
  const orgId = yield select(getOrgId);
  const scanDirectionState = yield select(getScanDirection);
  const scanDirection = action.payload.direction || scanDirectionState;
  const gateId = yield select(getGateId);
  const eventId = yield select(getEventId);

  const params:ScanTicketParams = {
    scanDirection,
    gateId,
    eventId,
    orgId,
    scanType,
    ticketNumber,
    bypassValidations
  };

  try {
    // Get current state
    const user: User = yield select(getUser);
    const selectedEvent: Event = yield select(getSelectedEvent);
    
    const result:ScanTicketMutation = yield call(api.scanTicket, params);
    if (result) {
      const response = result.scanTicket;
      if (response.status === ScanStatus.Error && !response.ticket &&
          response.problemCode !== ProblemCode.TicketNotFound) {
        if (!(yield select(getScanDismissed))) {
          yield put(actions.scanTicketOnline.failure(
            response.message || "Could not complete scan. Please try again."
          ));
        }
        return;
      }

      const { setStatus, setTicket, errorMsg } = determineTicketStatus(
        result,
        (user?.organization?.features ?? undefined) as FeatureFlagKeyEnum[] | undefined,
        selectedEvent
      );

      // Transform returned ticket into DeviceTicket type
      // so we only have to deal with a single format
      const ticket: DeviceTicket = {
        ticketNumber,
        ...setTicket as any, // gql types are not full compatible, so this just make the type parser happy considering we know this was previously working
        buyerInformation: setTicket?.order?.buyerInformation as Omit<BuyerInformation, '__typename'>,
        orderNumber: setTicket?.order?.orderNumber,
        ticketTypeName: setTicket?.ticketType?.name ?? setTicket?.membershipType?.name,
        isEventAddon: !!(setTicket as any)?.addon?.id && !setTicket?.ticketType,
        eventAddonName: (setTicket as any)?.addon?.name ,
        gateIds: (setTicket?.gates ?? []).map((gate: any) => gate.id),
        event: setTicket?.event as Ticket['event'],
      }

      // Generate time window message if needed
      let entryTimeMessage: string | undefined;
      if (result?.scanTicket?.problemCode === ProblemCode.OutsideEntryTime) {
        entryTimeMessage = result.scanTicket.message ?? 'Scanned outside of valid entry times'
      }

      const scannedTicket = { errorMsg, ticket, status: setStatus, entryTimeMessage };
      if (!(yield select(getScanDismissed))) {
        yield put(actions.scanTicketOnline.success(scannedTicket));
      }

      // Update local ticket with latest data
      yield put(actions.updateLocalTicket({ ticketNumber, ticket }));
    }
  } catch (error) {
    // If the network request fails, handle as we would offline;
    if ((error as Error)?.message.match(/network request failed/i)) {
      yield call(scanDeviceTicket, actions.scanDeviceTicket({ ticketNumber, scanType, direction: scanDirection }));
    } else {
      const errorMsg = getErrorMessage(error) || "Could not complete scan. Please try again.";
      if (!(yield select(getScanDismissed))) {
        yield put(actions.scanTicketOnline.failure(errorMsg));
      }
    }
  }
}

